import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { queryClient } from '@/lib/queryClient'
import { displayOrder } from '@/lib/layerLabels'
import type {
  User,
  Learner,
  AppModule,
  WorkspaceView,
  UserMode,
  EvaluationResults,
  WeekHistory,
  BookAnalysis,
  GeneratedGuide,
  ChatMessage,
  A2uiLayerPayload,
} from '@/types'

// ---------------------------------------------------------------------------
// Generated game entry — lives in Zustand during generation, source of truth
// for Theater Mode and the canvas. Persisted to localStorage on build_complete.
// ---------------------------------------------------------------------------

export interface GeneratedGameEntry {
  mechanic_id: string
  layer_name: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  layer: any // full layer payload from the backend
  htmlCode: string // accumulated HTML from code_delta chunks (legacy path)
  status: 'pending' | 'building' | 'complete' | 'failed'
  errorMessage?: string // populated when build_complete arrives with error: true
  // A2UI v1 (interactive games path). When set, the canvas should render via
  // A2uiRenderer instead of the iframe. Arrives via the `a2ui_layer` SSE event,
  // which fires immediately after the matching `layer` event. In interactive
  // mode no `build_start`/`code_delta`/`build_complete` events follow.
  a2uiPayload?: A2uiLayerPayload
}

// ---------------------------------------------------------------------------
// Chat threads (HOP-10) — one conversation per guide plus a platform thread.
// Keyed by guideId, or `platform:<learnerId|anon>` outside a guide. Held in
// memory (NOT localStorage); guide threads re-hydrate from the backend's
// ChatSession history when the guide is reopened.
// ---------------------------------------------------------------------------

export interface ChatThread {
  messages: ChatMessage[]
  sessionId: string | null
  // True once backend history has been fetched (or confirmed absent) — gates
  // the hydration query so it runs at most once per thread per app session.
  hydrated: boolean
  // True while an SSE response is streaming INTO this thread. Per-thread so
  // a stream in guide A never locks the input of guide B's thread.
  streaming: boolean
}

const EMPTY_THREAD: ChatThread = {
  messages: [],
  sessionId: null,
  hydrated: false,
  streaming: false,
}

// ---------------------------------------------------------------------------
// Generation progress panel — structured SSE state for the main UI
// ---------------------------------------------------------------------------

export interface LayerPlan {
  status: 'pending' | 'in_progress' | 'success' | 'error' | 'skipped'
  message: string
  details: string | null
  /** Engine pipeline phase the update came from: planning | generation |
   *  compilation | review. Lets the Generating screen distinguish "being
   *  planned" from "being generated" per layer. */
  phase?: string
}

export type GenerationPhase = 'idle' | 'generation_start' | 'planning' | 'review' | 'compiling' | 'complete'

function getNonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
}

function parseSseErrorMessage(data: string, fallback: string): string {
  const trimmed = data.trim()
  if (!trimmed) return fallback

  try {
    const parsed: unknown = JSON.parse(trimmed)
    if (typeof parsed === 'string') {
      return getNonEmptyString(parsed) ?? fallback
    }
    if (typeof parsed !== 'object' || parsed === null) {
      return fallback
    }

    const payload = parsed as Record<string, unknown>
    return (
      getNonEmptyString(payload.error_message) ??
      getNonEmptyString(payload.message) ??
      getNonEmptyString(payload.error) ??
      fallback
    )
  } catch {
    return trimmed
  }
}

// Pull the engine's structured failure_code out of an SSE error frame, if any.
// Used to message the user honestly (HOP-154 Fix C): an internal pipeline
// failure (`generation_failed`) must not be reported as "your chapter is too
// short" — only a genuine content shortfall (`insufficient_content`) should.
function parseSseFailureCode(data: string): string | null {
  const trimmed = data.trim()
  if (!trimmed) return null
  try {
    const parsed: unknown = JSON.parse(trimmed)
    if (typeof parsed !== 'object' || parsed === null) return null
    const payload = parsed as Record<string, unknown>
    return (
      getNonEmptyString(payload.failure_code) ??
      getNonEmptyString(payload.failureCode) ??
      null
    )
  } catch {
    return null
  }
}

/** Error carrying the engine's structured failure code through the stream. */
class GenerationError extends Error {
  failureCode: string | null
  constructor(message: string, failureCode: string | null) {
    super(message)
    this.name = 'GenerationError'
    this.failureCode = failureCode
  }
}

export interface GlobalAppState {
  // User & learner context
  user: User | null;
  activeLearnerId: string | null;
  activeLearner: Learner | null;

  // Navigation state
  activeModule: AppModule;
  activeWorkspaceItem: string | null;
  workspaceView: WorkspaceView;
  // Books surface — id of the book currently rendered in BOOK_DETAIL view.
  // Kept distinct from activeWorkspaceItem (which the MY_GUIDES module uses
  // for guideId/'new'/'evaluation-flow' etc.) so the two routings can't
  // collide.
  selectedBookId: string | null;

  // UI state
  mode: UserMode;
  leftPanelOpen: boolean;
  rightPanelOpen: boolean;
  isAiChatCollapsed: boolean;

  // Ask Hopscotch companion. Not persisted — the spec's idle state is the
  // edge handle, so every session starts closed.
  companionOpen: boolean;
  // Assistant messages that arrived while the panel was closed (drives the
  // coral unread dot on the handle — shown only when genuinely unread).
  companionUnread: number;
  // The context pill's × pressed: chat detaches from the open set (thread
  // falls back to the platform thread) until the next navigation.
  companionContextDetached: boolean;
  // Set while a game is being played full-screen (GamePreviewModal /
  // ContentPreviewModal mount). The companion is suppressed entirely.
  playingGameId: string | null;
  // print_set chat action → CanvasView opens the batch print preview for
  // this guide, then clears the request. Null = no pending request.
  printAllRequest: string | null;

  // Upload-a-book flow. Non-null = the full-page flow is open; the value is
  // the entry intent, which only branches the final landing + close target
  // (library → Book Analysis; home → generated set). Not persisted.
  uploadEntry: 'library' | 'home' | null;
  // Add-a-learner modal — hosted globally (ThreePanelLayout) so it can be
  // opened from the learner switcher and as the first-run prompt.
  addLearnerOpen: boolean;
  inviteRequired: boolean;

  // Module-specific state
  learningJourney: {
    currentWeek: number | null;
    weeklyHistory: WeekHistory[];
  };

  evaluation: {
    inProgress: boolean;
    results: EvaluationResults | null;
  };

  // Reading Guides state
  currentAnalysis: BookAnalysis | null;
  currentGuide: GeneratedGuide | null;
  cameFromPreview: boolean;

  // Chat messages (shared between RightPanel and SSE dispatchers)
  chatMessages: ChatMessage[];

  // Per-guide chat threads (HOP-10) — see ChatThread above.
  chatThreads: Record<string, ChatThread>;

  // Planning phase — proposed blueprint from the AI before execution
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  proposedPlan: any | null;

  // Reasoning block — collapsible log during generation
  isReasoning: boolean;
  reasoningStartTime: number | null;
  reasoningDuration: string | null;
  hiddenLogs: string[];
  statusHistory: string[];

  // Generation progress panel — structured status from SSE
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  bookDna: any | null;
  currentPhase: GenerationPhase;
  layerPlans: Record<string, LayerPlan>;

  // Theater mode — live generation state
  isGenerating: boolean;
  buildingNodeId: string | null;
  streamingCode: string;
  generatedGames: Record<string, GeneratedGameEntry>;
  activeGuideId: string | null; // the guide being generated
  // The full game list from the `blueprint` SSE event, in layer order —
  // known BEFORE any game builds, so the Generating screen can name every
  // row (queued) from the first frame. Empty until the blueprint arrives.
  blueprintLayers: { mechanicId: string; layerNumber: number; layerName: string | null }[];
  /** The approved plan's game lineup (proposed_plan event, post-critic) —
   *  same shape as blueprintLayers but available ~1/3 into the run. */
  plannedLayers: { mechanicId: string; layerNumber: number; layerName: string | null }[];
  /** Terminal generation failure (engine `error` SSE / dead stream) — the
   *  Generating screen shows the failure card instead of waiting forever. */
  streamError: string | null;
  /** Engine failure class for the terminal failure (`generation_failed`,
   *  `insufficient_content`, …) so the Generating screen can message honestly
   *  — an internal error must not be reported as "your chapter is too short"
   *  (HOP-154 Fix C). Null when unknown. */
  streamFailureCode: string | null;

  // Dev-only flag to prevent auto-select after reset
  devSkipAutoSelect: boolean;

  // Actions — Chat
  addChatMessage: (msg: ChatMessage) => void;
  clearChatMessages: () => void;

  // Actions — Chat threads (HOP-10)
  appendThreadMessage: (key: string, msg: ChatMessage) => void;
  appendToThreadMessage: (key: string, msgId: string, delta: string) => void;
  patchThreadMessage: (key: string, msgId: string, updates: Partial<ChatMessage>) => void;
  removeThreadMessage: (key: string, msgId: string) => void;
  setThreadSession: (key: string, sessionId: string | null) => void;
  setThreadStreaming: (key: string, streaming: boolean) => void;
  hydrateThread: (key: string, data: { messages: ChatMessage[]; sessionId: string | null }) => void;
  resetThread: (key: string) => void;

  // Actions — Theater mode
  setBuildingNodeId: (id: string | null) => void;
  appendStreamingCode: (chunk: string) => void;
  addGeneratedGame: (entry: GeneratedGameEntry) => void;
  updateGeneratedGame: (mechanic_id: string, updates: Partial<GeneratedGameEntry>) => void;
  clearGeneratedGames: () => void;
  streamGameGeneration: (
    payload: {
      raw_input_text: string
      book_title: string
      learner_stage: number | null
      // Optional on the wire; the normal flows always pass both. The admin
      // /admin/generate test page omits them (no learner ⇒ no recency
      // context engine-side, which is what a deterministic test wants).
      tenant_id?: string
      learner_id?: string
      review_mode: 'auto'
      // ADMIN-ONLY diagnostic (backend 403s others): force exactly these
      // mechanics — ai-service sends only their templates to the engine.
      forced_mechanics?: string[]
      // When true, engine emits both `layer` and `a2ui_layer` events for each
      // mechanic and ai-service skips Anthropic codegen. Defaults to false on
      // the wire if omitted. Gated FE-side by `featureFlags.A2UI_ENABLED`.
      is_interactive?: boolean
    },
    // Required: the existing guide row created during the upload phase. Reusing
    // it (instead of creating a fresh row here) is what prevents the duplicate
    // pair documented in reading_guides_duplicate_fix_frontend_handoff.md.
    guideId: string,
    onNavigate: (guideId: string) => void,
  ) => void;
  // Append ONE new game to an already-generated guide (chat "add another
  // game"). Unlike streamGameGeneration it never clears generatedGames or
  // chat — the canvas keeps its saved games and the new node appears live.
  appendGameGeneration: (
    guideId: string,
    params?: {
      category?: 'pattern_practice' | 'vocabulary' | 'fluency' | 'comprehension'
      focus?: string
    },
  ) => void;

  // Actions — User
  setUser: (user: User | null) => void;
  logout: () => void;

  // Actions — Learner
  setActiveLearner: (learnerId: string | null, learner: Learner | null) => void;
  updateLearner: (updates: Partial<Learner>) => void;
  setAddLearnerOpen: (open: boolean) => void;
  /** Auth0-authenticated but the backend refused account creation without a
   *  valid invite (403 INVITE_REQUIRED) — ProtectedRoute shows the invite
   *  wall. Not persisted. */
  setInviteRequired: (required: boolean) => void;

  // Actions — Navigation
  setActiveModule: (module: AppModule) => void;
  setActiveWorkspaceItem: (itemId: string | null) => void;
  setWorkspaceView: (view: WorkspaceView) => void;
  setSelectedBookId: (id: string | null) => void;

  // Actions — UI
  setMode: (mode: UserMode) => void;
  toggleLeftPanel: () => void;
  toggleRightPanel: () => void;
  setLeftPanelOpen: (open: boolean) => void;
  setRightPanelOpen: (open: boolean) => void;
  toggleAiChat: () => void;

  // Actions — Ask Hopscotch companion
  setCompanionOpen: (open: boolean) => void;
  setCompanionContextDetached: (detached: boolean) => void;
  setPlayingGame: (gameId: string | null) => void;
  requestPrintAll: (guideId: string) => void;
  clearPrintAllRequest: () => void;

  // Actions — Upload flow
  openUpload: (entry: 'library' | 'home') => void;
  closeUpload: () => void;

  // Actions — Reading Guides
  setCurrentAnalysis: (analysis: BookAnalysis | null) => void;
  setCurrentGuide: (guide: GeneratedGuide | null) => void;
  setCameFromPreview: (value: boolean) => void;

  // Actions — Dev
  setDevSkipAutoSelect: (value: boolean) => void;

  // Actions — Evaluation
  setEvaluationInProgress: (inProgress: boolean) => void;
  setEvaluationResults: (results: EvaluationResults | null) => void;

  // Actions — Learning Journey
  setCurrentWeek: (week: number | null) => void;
  setWeeklyHistory: (history: WeekHistory[]) => void;
}

export const useGlobalState = create<GlobalAppState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      activeLearnerId: null,
      activeLearner: null,
      activeModule: 'MY_GUIDES',
      activeWorkspaceItem: null,
      workspaceView: 'GALLERY',
      selectedBookId: null,
      mode: 'PARENT',
      leftPanelOpen: false,
      rightPanelOpen: false,
      isAiChatCollapsed: false,
      companionOpen: false,
      companionUnread: 0,
      companionContextDetached: false,
      playingGameId: null,
      printAllRequest: null,
      uploadEntry: null,
      addLearnerOpen: false,
      inviteRequired: false,
      learningJourney: {
        currentWeek: null,
        weeklyHistory: [],
      },
      evaluation: {
        inProgress: false,
        results: null,
      },
      currentAnalysis: null,
      currentGuide: null,
      cameFromPreview: false,
      chatMessages: [],
      proposedPlan: null,
      isReasoning: false,
      reasoningStartTime: null,
      reasoningDuration: null,
      hiddenLogs: [],
      statusHistory: [],
      bookDna: null,
      currentPhase: 'idle' as GenerationPhase,
      layerPlans: {},
      isGenerating: false,
      buildingNodeId: null,
      streamingCode: '',
      generatedGames: {},
      activeGuideId: null,
      blueprintLayers: [],
      plannedLayers: [],
      streamError: null,
      streamFailureCode: null,
      devSkipAutoSelect: false,

      // Actions — Chat
      addChatMessage: (msg) =>
        set((state) => ({ chatMessages: [...state.chatMessages, msg] })),
      clearChatMessages: () => set({ chatMessages: [] }),

      // Actions — Chat threads (HOP-10). All writes go through the thread
      // KEY so a stream started in one thread keeps writing there even if
      // the user navigates to another thread mid-stream.
      chatThreads: {},
      appendThreadMessage: (key, msg) =>
        set((state) => {
          const t = state.chatThreads[key] ?? EMPTY_THREAD
          return {
            chatThreads: {
              ...state.chatThreads,
              [key]: { ...t, messages: [...t.messages, msg] },
            },
            // Unread dot: full assistant messages landing while the panel is
            // closed (generation notices, replies finishing in background).
            // The content guard skips empty streaming placeholders.
            companionUnread:
              !state.companionOpen && msg.role === 'assistant' && msg.content
                ? state.companionUnread + 1
                : state.companionUnread,
          }
        }),
      appendToThreadMessage: (key, msgId, delta) =>
        set((state) => {
          const t = state.chatThreads[key]
          if (!t) return state
          return {
            chatThreads: {
              ...state.chatThreads,
              [key]: {
                ...t,
                messages: t.messages.map((m) =>
                  m.id === msgId ? { ...m, content: m.content + delta } : m
                ),
              },
            },
          }
        }),
      patchThreadMessage: (key, msgId, updates) =>
        set((state) => {
          const t = state.chatThreads[key]
          if (!t) return state
          return {
            chatThreads: {
              ...state.chatThreads,
              [key]: {
                ...t,
                messages: t.messages.map((m) =>
                  m.id === msgId ? { ...m, ...updates } : m
                ),
              },
            },
          }
        }),
      removeThreadMessage: (key, msgId) =>
        set((state) => {
          const t = state.chatThreads[key]
          if (!t) return state
          return {
            chatThreads: {
              ...state.chatThreads,
              [key]: { ...t, messages: t.messages.filter((m) => m.id !== msgId) },
            },
          }
        }),
      setThreadSession: (key, sessionId) =>
        set((state) => {
          const t = state.chatThreads[key] ?? EMPTY_THREAD
          return { chatThreads: { ...state.chatThreads, [key]: { ...t, sessionId } } }
        }),
      setThreadStreaming: (key, streaming) =>
        set((state) => {
          const t = state.chatThreads[key] ?? EMPTY_THREAD
          return { chatThreads: { ...state.chatThreads, [key]: { ...t, streaming } } }
        }),
      hydrateThread: (key, data) =>
        set((state) => {
          const t = state.chatThreads[key]
          // Live state wins: if the thread already has messages (the user
          // chatted before hydration resolved), just mark it hydrated —
          // merging server history under live messages would duplicate the
          // just-sent exchange (it exists locally AND server-side).
          if (t && t.messages.length > 0) {
            return { chatThreads: { ...state.chatThreads, [key]: { ...t, hydrated: true } } }
          }
          return {
            chatThreads: {
              ...state.chatThreads,
              [key]: {
                messages: data.messages,
                sessionId: data.sessionId ?? t?.sessionId ?? null,
                hydrated: true,
                streaming: t?.streaming ?? false,
              },
            },
          }
        }),
      resetThread: (key) =>
        set((state) => ({
          chatThreads: {
            ...state.chatThreads,
            // hydrated stays true: clearing a thread must not resurrect the
            // old server history on the next render. The next send starts a
            // fresh backend session (sessionId null).
            [key]: { messages: [], sessionId: null, hydrated: true, streaming: false },
          },
        })),

      // Actions — Theater mode
      setBuildingNodeId: (id) => set({ buildingNodeId: id, streamingCode: '' }),
      appendStreamingCode: (chunk) =>
        set((state) => ({ streamingCode: state.streamingCode + chunk })),

      addGeneratedGame: (entry) =>
        set((state) => ({
          generatedGames: { ...state.generatedGames, [entry.mechanic_id]: entry },
        })),

      updateGeneratedGame: (mechanic_id, updates) =>
        set((state) => {
          const existing = state.generatedGames[mechanic_id]
          if (!existing) return state
          return {
            generatedGames: {
              ...state.generatedGames,
              [mechanic_id]: { ...existing, ...updates },
            },
          }
        }),

      clearGeneratedGames: () => set({ generatedGames: {}, activeGuideId: null }),

      streamGameGeneration: (payload, guideId, onNavigate) => {
        // Clean previous state
        get().clearChatMessages()
        get().clearGeneratedGames()
        set({
          isGenerating: true,
          isReasoning: true,
          reasoningStartTime: Date.now(),
          reasoningDuration: null,
          hiddenLogs: [],
          statusHistory: [],
          proposedPlan: null,
          bookDna: null,
          currentPhase: 'generation_start' as GenerationPhase,
          layerPlans: {},
          blueprintLayers: [],
          plannedLayers: [],
          streamError: null,
          streamFailureCode: null,
        })

        let navigated = false

        const run = async () => {
          // Reuse the guideId from the upload phase. That row already carries
          // mergedOcrText + uploaded images; ai-service enriches it with
          // book_dna / blueprint / layers via the SSE persistence path. Creating
          // a second row here was the bug fixed in
          // reading_guides_duplicate_fix_frontend_handoff.md.
          set({ activeGuideId: guideId })

          // Open the SSE stream WITH guide_id so ai-service auto-persists.
          const { getAccessToken } = await import('@/lib/apiClient')
          const token = await getAccessToken()
          const baseUrl = import.meta.env.VITE_API_URL_AI
          const response = await fetch(`${baseUrl}/api/generate/activities`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ ...payload, guide_id: guideId }),
          })

          if (!response.ok) throw new Error(`Generation failed (${response.status})`)

          const reader = response.body?.getReader()
          if (!reader) throw new Error('No response stream')

          const decoder = new TextDecoder()
          let buffer = ''
          let currentEvent = ''

          while (true) {
            const { done, value } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() ?? ''

            for (const line of lines) {
              if (line.startsWith('event:')) {
                currentEvent = line.slice(6).trim()
              } else if (line.startsWith('data:')) {
                const data = line.slice(5).trim()

                if (currentEvent === 'progress') {
                  // Consumed by UI
                } else if (currentEvent === 'proposed_plan') {
                  // The approved plan (emitted by exec_fan_out, post-critic).
                  // Arrives ~1/3 into the run — the Generating screen names
                  // its per-game rows from this, long before the compiled
                  // blueprint exists.
                  try {
                    const parsed = JSON.parse(data)
                    const pb = (parsed.proposed_blueprint ?? {}) as Record<
                      string,
                      { mechanic_id?: string; display_name?: string; layer_number?: number }
                    >
                    const planned = Object.values(pb)
                      .filter((p) => p && p.mechanic_id && typeof p.layer_number === 'number')
                      .map((p) => ({
                        mechanicId: p.mechanic_id!,
                        layerNumber: p.layer_number!,
                        layerName: p.display_name ?? null,
                      }))
                      .sort((a, b) => displayOrder(a.layerNumber) - displayOrder(b.layerNumber))
                    set({
                      proposedPlan: parsed.proposed_blueprint,
                      ...(planned.length > 0 ? { plannedLayers: planned } : {}),
                    })
                  } catch { /* skip malformed plan */ }
                } else if (currentEvent === 'ui_status') {
                  try {
                    const parsed = JSON.parse(data)
                    const message = parsed.message ?? data
                    set((state) => ({
                      statusHistory: [...state.statusHistory, message],
                    }))
                  } catch {
                    set((state) => ({
                      statusHistory: [...state.statusHistory, data],
                    }))
                  }
                } else if (currentEvent === 'book_dna') {
                  try {
                    set({ bookDna: JSON.parse(data) })
                  } catch { /* skip */ }
                } else if (currentEvent === 'status_update') {
                  try {
                    const parsed = JSON.parse(data)
                    set((state) => ({
                      currentPhase: parsed.phase ?? state.currentPhase,
                      layerPlans: parsed.layer
                        ? {
                            ...state.layerPlans,
                            [parsed.layer]: {
                              status: parsed.status ?? 'pending',
                              message: parsed.message ?? '',
                              details: parsed.details ?? null,
                              phase: parsed.phase ?? undefined,
                            },
                          }
                        : state.layerPlans,
                    }))
                  } catch { /* skip */ }
                } else if (currentEvent === 'blueprint') {
                  // Capture the full game list (layer order) — the Generating
                  // screen names every row from this, before any game builds.
                  try {
                    const blueprint = JSON.parse(data) as {
                      layers?: { mechanic_id?: string; layer_number?: number; layer_name?: string | null }[]
                    }
                    const layers = (blueprint.layers ?? [])
                      .filter((l) => l.mechanic_id && typeof l.layer_number === 'number')
                      .map((l) => ({
                        mechanicId: l.mechanic_id!,
                        layerNumber: l.layer_number!,
                        layerName: l.layer_name ?? null,
                      }))
                      .sort((a, b) => displayOrder(a.layerNumber) - displayOrder(b.layerNumber))
                    if (layers.length > 0) set({ blueprintLayers: layers })
                  } catch { /* screen falls back to arrival-order rows */ }

                  // Blueprint = navigate immediately, games will appear as layers arrive
                  if (!navigated && guideId) {
                    navigated = true
                    onNavigate(guideId)
                  }
                } else if (currentEvent === 'layer') {
                  // Full layer with ui_components — add as a pending game
                  try {
                    const layer = JSON.parse(data)
                    get().addGeneratedGame({
                      mechanic_id: layer.mechanic_id,
                      layer_name: layer.layer_name ?? 'Activity',
                      layer,
                      htmlCode: '',
                      status: 'pending',
                    })
                  } catch { /* skip */ }

                  // If blueprint never fired, navigate on first layer
                  if (!navigated && guideId) {
                    navigated = true
                    onNavigate(guideId)
                  }
                } else if (currentEvent === 'a2ui_layer') {
                  // Interactive games path: arrives immediately after the
                  // matching `layer` event. Mark the entry complete and stash
                  // the payload — the canvas renderer will pick the React
                  // component path instead of waiting for HTML build_*/code_delta.
                  try {
                    const payload = JSON.parse(data) as A2uiLayerPayload
                    const existing = get().generatedGames[payload.mechanic_id]
                    if (existing) {
                      get().updateGeneratedGame(payload.mechanic_id, {
                        a2uiPayload: payload,
                        status: 'complete',
                      })
                    } else {
                      // Defensive: `layer` event was dropped or arrived out of
                      // order. Create the entry from the a2ui payload directly
                      // so the node still renders.
                      get().addGeneratedGame({
                        mechanic_id: payload.mechanic_id,
                        layer_name: payload.layer_name ?? 'Activity',
                        layer: payload.props,
                        htmlCode: '',
                        status: 'complete',
                        a2uiPayload: payload,
                      })
                      if (!navigated && guideId) {
                        navigated = true
                        onNavigate(guideId)
                      }
                    }
                  } catch { /* skip malformed */ }
                } else if (currentEvent === 'code_delta') {
                  // Append to streamingCode (for Theater Mode live preview)
                  try {
                    const parsedChunk = JSON.parse(data)
                    get().appendStreamingCode(parsedChunk)
                  } catch {
                    get().appendStreamingCode(data)
                  }

                  // Also accumulate into the game's htmlCode
                  const nodeId = get().buildingNodeId
                  if (nodeId) {
                    const existing = get().generatedGames[nodeId]
                    if (existing) {
                      try {
                        const chunk = JSON.parse(data)
                        get().updateGeneratedGame(nodeId, {
                          htmlCode: existing.htmlCode + chunk,
                        })
                      } catch {
                        get().updateGeneratedGame(nodeId, {
                          htmlCode: existing.htmlCode + data,
                        })
                      }
                    }
                  }
                } else if (currentEvent === 'build_start') {
                  set({ streamingCode: '' })
                  let mechanicId = data
                  try {
                    const parsed = JSON.parse(data)
                    mechanicId = parsed.mechanic_id ?? parsed.id ?? data
                  } catch { /* use raw data */ }

                  get().setBuildingNodeId(mechanicId)
                  get().updateGeneratedGame(mechanicId, { status: 'building' })
                } else if (currentEvent === 'build_complete') {
                  // Parse for mechanic_id + error flag. Spec §4.3: skipped-layer
                  // builds emit { mechanic_id, error: true } even when build_start
                  // never fired, so we can't rely on buildingNodeId alone.
                  let parsedMechanicId: string | undefined
                  let isError = false
                  let errorMessage: string | undefined
                  try {
                    const parsed = JSON.parse(data)
                    parsedMechanicId = parsed.mechanic_id ?? parsed.id
                    isError = parsed.error === true
                    errorMessage = parsed.error_message ?? parsed.message
                  } catch { /* fall back to buildingNodeId */ }

                  const nodeId = parsedMechanicId ?? get().buildingNodeId
                  const code = get().streamingCode

                  if (nodeId) {
                    if (isError) {
                      get().updateGeneratedGame(nodeId, {
                        status: 'failed',
                        errorMessage: errorMessage ?? 'This activity could not be built.',
                      })
                    } else {
                      const finalHtml = code || get().generatedGames[nodeId]?.htmlCode || ''
                      get().updateGeneratedGame(nodeId, {
                        status: 'complete',
                        htmlCode: finalHtml,
                      })
                      // Backend now owns canonical persistence — written server-side
                      // on every build_complete. No FE persistence needed.
                    }
                  }
                  set({ buildingNodeId: null, streamingCode: '' })
                } else if (currentEvent === 'error') {
                  throw new GenerationError(
                    parseSseErrorMessage(data, 'Server reported a generation error.'),
                    parseSseFailureCode(data),
                  )
                }
              }
            }
          }

          // Stream finished — ensure navigation happened.
          if (!navigated) {
            navigated = true
            onNavigate(guideId)
          }

          // Finalize reasoning block
          const startTime = get().reasoningStartTime
          let duration = ''
          if (startTime) {
            const diffMs = Date.now() - startTime
            const totalSec = Math.round(diffMs / 1000)
            const min = Math.floor(totalSec / 60)
            const sec = totalSec % 60
            duration = min > 0 ? `${min} min ${sec} sec` : `${sec} sec`
          }

          set({
            isGenerating: false,
            isReasoning: false,
            reasoningDuration: duration,
            currentPhase: 'complete' as GenerationPhase,
          })

          // Completion notice lands in this guide's chat thread (HOP-10)
          get().appendThreadMessage(guideId, {
            id: `complete-${Date.now()}`,
            role: 'assistant',
            content: `Build complete! ${Object.keys(get().generatedGames).length} games generated.`,
            timestamp: new Date(),
          })

          console.log('[StreamGen] Complete:', Object.keys(get().generatedGames).length, 'games')

          // Refetch canonical state from the server now that backend persistence
          // is settled. This refreshes the canvas to match the DB (final
          // generationStatus, server-rendered htmlCode, generatedAt timestamp)
          // and refreshes the gallery so the new guide shows up.
          queryClient.invalidateQueries({ queryKey: ['reading-guide', guideId] })
          queryClient.invalidateQueries({ queryKey: ['reading-guides'] })
        }

        run().catch((err) => {
          console.error('[StreamGen] Stream failed:', err)
          set({
            isGenerating: false,
            isReasoning: false,
            streamError:
              err instanceof Error ? err.message : 'Generation failed. Please try again.',
            streamFailureCode:
              err instanceof GenerationError ? err.failureCode : null,
          })
          // Surface failure to the user via the guide's chat thread. We
          // deliberately do NOT fire onNavigate on failure — the caller stays
          // in their current view (e.g. FindingsStep) so the user can retry.
          // If we already navigated to the canvas before the failure, the
          // canvas shows whatever partial state landed.
          get().appendThreadMessage(guideId, {
            id: `error-${Date.now()}`,
            role: 'assistant',
            content:
              err instanceof Error
                ? `Generation failed: ${err.message}`
                : 'Generation failed. Please try again.',
            timestamp: new Date(),
          })

          // Refetch the guide row so the canvas (and any other consumer) picks
          // up generationStatus=FAILED and the persisted error message.
          queryClient.invalidateQueries({ queryKey: ['reading-guide', guideId] })
          queryClient.invalidateQueries({ queryKey: ['reading-guides'] })
        })
      },

      appendGameGeneration: (guideId, params) => {
        if (get().isGenerating) {
          console.warn('[AddGame] Generation already running — ignoring request')
          return
        }
        // Drop another guide's leftover live games so they don't leak onto this
        // guide's canvas (generatedGames is shared). Same-guide adds keep them.
        if (get().activeGuideId !== guideId) {
          set({ generatedGames: {} })
        }
        // Append mode: keep existing generatedGames + chat. activeGuideId
        // gates the canvas's live-entry overlay to this guide.
        set({
          isGenerating: true,
          isReasoning: true,
          reasoningStartTime: Date.now(),
          reasoningDuration: null,
          currentPhase: 'generation_start' as GenerationPhase,
          activeGuideId: guideId,
        })

        const run = async () => {
          const { getAccessToken } = await import('@/lib/apiClient')
          const { A2UI_ENABLED } = await import('@/lib/featureFlags')
          const token = await getAccessToken()
          const baseUrl = import.meta.env.VITE_API_URL_AI
          const response = await fetch(`${baseUrl}/api/generate/game`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              guide_id: guideId,
              category: params?.category,
              focus: params?.focus,
              is_interactive: A2UI_ENABLED,
            }),
          })

          if (!response.ok) {
            const body = await response.json().catch(() => null)
            throw new Error(body?.error ?? `Add game failed (${response.status})`)
          }

          const reader = response.body?.getReader()
          if (!reader) throw new Error('No response stream')

          const decoder = new TextDecoder()
          let buffer = ''
          let currentEvent = ''

          while (true) {
            const { done, value } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() ?? ''

            for (const line of lines) {
              if (line.startsWith('event:')) {
                currentEvent = line.slice(6).trim()
              } else if (line.startsWith('data:')) {
                const data = line.slice(5).trim()

                if (currentEvent === 'status_update') {
                  try {
                    const parsed = JSON.parse(data)
                    set((state) => ({
                      currentPhase: parsed.phase ?? state.currentPhase,
                    }))
                  } catch { /* skip */ }
                } else if (currentEvent === 'layer') {
                  try {
                    const layer = JSON.parse(data)
                    get().addGeneratedGame({
                      mechanic_id: layer.mechanic_id,
                      layer_name: layer.layer_name ?? 'Activity',
                      layer,
                      htmlCode: '',
                      status: 'pending',
                    })
                  } catch { /* skip */ }
                } else if (currentEvent === 'a2ui_layer') {
                  try {
                    const payload = JSON.parse(data) as A2uiLayerPayload
                    const existing = get().generatedGames[payload.mechanic_id]
                    if (existing) {
                      get().updateGeneratedGame(payload.mechanic_id, {
                        a2uiPayload: payload,
                        status: 'complete',
                      })
                    } else {
                      get().addGeneratedGame({
                        mechanic_id: payload.mechanic_id,
                        layer_name: payload.layer_name ?? 'Activity',
                        layer: payload.props,
                        htmlCode: '',
                        status: 'complete',
                        a2uiPayload: payload,
                      })
                    }
                  } catch { /* skip malformed */ }
                } else if (currentEvent === 'code_delta') {
                  try {
                    get().appendStreamingCode(JSON.parse(data))
                  } catch {
                    get().appendStreamingCode(data)
                  }
                  const nodeId = get().buildingNodeId
                  if (nodeId) {
                    const existing = get().generatedGames[nodeId]
                    if (existing) {
                      try {
                        const chunk = JSON.parse(data)
                        get().updateGeneratedGame(nodeId, {
                          htmlCode: existing.htmlCode + chunk,
                        })
                      } catch {
                        get().updateGeneratedGame(nodeId, {
                          htmlCode: existing.htmlCode + data,
                        })
                      }
                    }
                  }
                } else if (currentEvent === 'build_start') {
                  set({ streamingCode: '' })
                  let mechanicId = data
                  try {
                    const parsed = JSON.parse(data)
                    mechanicId = parsed.mechanic_id ?? parsed.id ?? data
                  } catch { /* use raw data */ }
                  get().setBuildingNodeId(mechanicId)
                  get().updateGeneratedGame(mechanicId, { status: 'building' })
                } else if (currentEvent === 'build_complete') {
                  let parsedMechanicId: string | undefined
                  let isError = false
                  let errorMessage: string | undefined
                  try {
                    const parsed = JSON.parse(data)
                    parsedMechanicId = parsed.mechanic_id ?? parsed.id
                    isError = parsed.error === true
                    errorMessage = parsed.error_message ?? parsed.message
                  } catch { /* fall back to buildingNodeId */ }

                  const nodeId = parsedMechanicId ?? get().buildingNodeId
                  const code = get().streamingCode
                  if (nodeId) {
                    if (isError) {
                      get().updateGeneratedGame(nodeId, {
                        status: 'failed',
                        errorMessage: errorMessage ?? 'This activity could not be built.',
                      })
                    } else {
                      const finalHtml = code || get().generatedGames[nodeId]?.htmlCode || ''
                      get().updateGeneratedGame(nodeId, {
                        status: 'complete',
                        htmlCode: finalHtml,
                      })
                    }
                  }
                  set({ buildingNodeId: null, streamingCode: '' })
                } else if (currentEvent === 'error') {
                  throw new Error(
                    parseSseErrorMessage(data, 'Server reported an add-game error.'),
                  )
                }
              }
            }
          }

          set({
            isGenerating: false,
            isReasoning: false,
            currentPhase: 'complete' as GenerationPhase,
          })

          get().appendThreadMessage(guideId, {
            id: `addgame-complete-${Date.now()}`,
            role: 'assistant',
            content: 'New game added to your guide!',
            timestamp: new Date(),
          })

          queryClient.invalidateQueries({ queryKey: ['reading-guide', guideId] })
        }

        run().catch((err) => {
          console.error('[AddGame] Stream failed:', err)
          set({ isGenerating: false, isReasoning: false })
          get().appendThreadMessage(guideId, {
            id: `addgame-error-${Date.now()}`,
            role: 'assistant',
            content:
              err instanceof Error
                ? `Couldn't add a game: ${err.message}`
                : "Couldn't add a game. Please try again.",
            timestamp: new Date(),
          })
          queryClient.invalidateQueries({ queryKey: ['reading-guide', guideId] })
        })
      },

      // Actions — User
      setUser: (user) => set({ user }),
      logout: () =>
        set({
          user: null,
          activeLearnerId: null,
          activeLearner: null,
          activeModule: 'MY_GUIDES',
          activeWorkspaceItem: null,
          workspaceView: 'GALLERY',
          selectedBookId: null,
          evaluation: { inProgress: false, results: null },
          learningJourney: { currentWeek: null, weeklyHistory: [] },
        }),

      // Actions — Learner
      setActiveLearner: (learnerId, learner) =>
        set({
          activeLearnerId: learnerId,
          activeLearner: learner,
          activeWorkspaceItem: null,
          workspaceView: 'GALLERY',
          selectedBookId: null,
          activeModule: 'MY_GUIDES',
          evaluation: { inProgress: false, results: null },
          learningJourney: { currentWeek: null, weeklyHistory: [] },
        }),
      updateLearner: (updates) =>
        set((state) => ({
          activeLearner: state.activeLearner
            ? { ...state.activeLearner, ...updates }
            : null,
        })),
      setAddLearnerOpen: (open) => set({ addLearnerOpen: open }),
      setInviteRequired: (required) => set({ inviteRequired: required }),

      // Actions — Navigation. Both re-attach companion context: the pill's ×
      // detaches only until the user goes somewhere else.
      setActiveModule: (module) =>
        set({
          activeModule: module,
          activeWorkspaceItem: null,
          workspaceView: 'GALLERY',
          selectedBookId: null,
          companionContextDetached: false,
        }),
      setActiveWorkspaceItem: (itemId) =>
        set({ activeWorkspaceItem: itemId, companionContextDetached: false }),
      setWorkspaceView: (view) => set({ workspaceView: view }),
      setSelectedBookId: (id) => set({ selectedBookId: id }),

      // Actions — UI
      setMode: (mode) => set({ mode }),
      toggleLeftPanel: () =>
        set((state) => ({ leftPanelOpen: !state.leftPanelOpen })),
      toggleRightPanel: () =>
        set((state) => ({ rightPanelOpen: !state.rightPanelOpen })),
      setLeftPanelOpen: (open) => set({ leftPanelOpen: open }),
      setRightPanelOpen: (open) => set({ rightPanelOpen: open }),
      toggleAiChat: () =>
        set((state) => ({ isAiChatCollapsed: !state.isAiChatCollapsed })),

      // Actions — Ask Hopscotch companion
      setCompanionOpen: (open) =>
        set((state) => ({
          companionOpen: open,
          companionUnread: open ? 0 : state.companionUnread,
        })),
      setCompanionContextDetached: (detached) =>
        set({ companionContextDetached: detached }),
      setPlayingGame: (gameId) => set({ playingGameId: gameId }),
      requestPrintAll: (guideId) => set({ printAllRequest: guideId }),
      clearPrintAllRequest: () => set({ printAllRequest: null }),

      // Actions — Upload flow
      openUpload: (entry) => set({ uploadEntry: entry }),
      closeUpload: () => set({ uploadEntry: null }),

      // Actions — Reading Guides
      setCurrentAnalysis: (analysis) => set({ currentAnalysis: analysis }),
      setCurrentGuide: (guide) => set({ currentGuide: guide }),
      setCameFromPreview: (value) => set({ cameFromPreview: value }),

      // Actions — Dev
      setDevSkipAutoSelect: (value) => set({ devSkipAutoSelect: value }),

      // Actions — Evaluation
      setEvaluationInProgress: (inProgress) =>
        set((state) => ({
          evaluation: { ...state.evaluation, inProgress },
        })),
      setEvaluationResults: (results) =>
        set((state) => ({
          evaluation: { ...state.evaluation, results },
        })),

      // Actions — Learning Journey
      setCurrentWeek: (week) =>
        set((state) => ({
          learningJourney: { ...state.learningJourney, currentWeek: week },
        })),
      setWeeklyHistory: (history) =>
        set((state) => ({
          learningJourney: { ...state.learningJourney, weeklyHistory: history },
        })),
    }),
    {
      name: 'hopscotch-hub-state',
      version: 4,
      migrate: (persisted: unknown) => {
        // Strip the stale active-learner *snapshot* (it can go stale between
        // sessions). The lightweight activeLearnerId is persisted and used to
        // re-hydrate the snapshot from the learner list on load.
        const { activeLearner: _activeLearner, ...rest } =
          persisted as Record<string, unknown>
        void _activeLearner
        return rest
      },
      partialize: (state) => ({
        user: state.user,
        mode: state.mode,
        isAiChatCollapsed: state.isAiChatCollapsed,
        // Remember the last learner the user had selected so we can
        // pre-select them on the next load.
        activeLearnerId: state.activeLearnerId,
      }),
    }
  )
)

